import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DocumentMimeKind, FindHint } from '../types';
import {
    collapseOverlappingHits,
    FindHit,
    HIT_CAP,
    MIN_QUERY_LEN,
    looksLikeMoneyQuery,
    looksLikeSsnQuery,
    PageTextCache,
    searchDocumentCache,
    searchSsnLocatePatterns
} from './find-engine';

export interface MatchedField {
    hint: FindHint;
    count: number;
    hits: FindHit[];
    truncated: boolean;
}

export type FieldStatus = 'pending' | 'unavailable' | 'found' | 'likely' | 'missing';

export function useLocate(args: {
    findHints: FindHint[];
    autoSelectRequired?: boolean;
    url: string;
    mimeKind: DocumentMimeKind;
    loading: boolean;
    error: string | null;
    textReady: boolean;
    hasText: boolean;
    ocrRunning: boolean;
    aligning: boolean;
    allowOcr?: boolean;
    ocrNote: string;
    pageCacheRef: React.MutableRefObject<PageTextCache[]>;
    pageIndex: number;
    setPageIndex: (n: number | ((p: number) => number)) => void;
    paintHighlights: () => void;
    verifyLabel?: string;
    showLocate?: boolean;
    scanTick?: number;
}) {
    const hitsRef = useRef<FindHit[]>([]);
    const activeIndexRef = useRef(0);
    const pendingFocusRef = useRef(false);
    const autoSelectKeyRef = useRef('');
    const [hits, setHits] = useState<FindHit[]>([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [truncated, setTruncated] = useState(false);
    const [searchStatus, setSearchStatus] = useState('');
    const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
    const [matchedFields, setMatchedFields] = useState<MatchedField[]>([]);
    const [customQuery, setCustomQuery] = useState('');
    const [locateSource, setLocateSource] = useState<'field' | 'custom' | null>(null);

    const clear = useCallback(() => {
        setHits([]);
        hitsRef.current = [];
        setActiveIndex(0);
        activeIndexRef.current = 0;
        setTruncated(false);
        setSearchStatus('');
        setActiveFieldId(null);
        setLocateSource(null);
        setCustomQuery('');
        autoSelectKeyRef.current = '';
        pendingFocusRef.current = false;
        setMatchedFields([]);
    }, []);

    const applyHits = useCallback((nextHits: FindHit[], isTruncated: boolean, focus: boolean) => {
        setHits(nextHits);
        hitsRef.current = nextHits;
        setTruncated(isTruncated);
        if (!nextHits.length) {
            setActiveIndex(0);
            activeIndexRef.current = 0;
            setSearchStatus('No matches');
            return;
        }
        setActiveIndex(0);
        activeIndexRef.current = 0;
        if (focus) {
            pendingFocusRef.current = true;
        }
        setSearchStatus(
            isTruncated
                ? `Match 1 of ${nextHits.length} (first ${HIT_CAP})`
                : `Match 1 of ${nextHits.length}`
        );
        const first = nextHits[0];
        args.setPageIndex(prev => (first.pageIndex !== prev ? first.pageIndex : prev));
    }, [args]);

    useEffect(() => {
        if (args.loading || args.error) {
            setMatchedFields([]);
            return;
        }
        if ((args.mimeKind === 'image' || args.mimeKind === 'pdf') && !args.textReady && args.findHints.length > 0) {
            setMatchedFields(args.findHints.map(hint => ({
                hint,
                count: 0,
                hits: [],
                truncated: false
            })));
            return;
        }
        if (!args.textReady || (args.mimeKind !== 'pdf' && args.mimeKind !== 'image')) {
            setMatchedFields([]);
            return;
        }
        const pages = args.pageCacheRef.current;
        const matched: MatchedField[] = [];
        for (let i = 0; i < args.findHints.length; i++) {
            const hint = args.findHints[i];
            const hitMap: { [k: string]: FindHit } = {};
            let trunc = false;

            if (hint.locateKind === 'ssnPattern') {
                const result = searchSsnLocatePatterns(pages);
                trunc = result.truncated;
                for (let h = 0; h < result.hits.length; h++) {
                    const hit = result.hits[h];
                    const key = hit.pageIndex + ':' + hit.start + ':' + hit.end;
                    if (!hitMap[key]) {
                        hitMap[key] = hit;
                    }
                }
            } else {
                const queries = (hint.variants && hint.variants.length) ? hint.variants : [hint.value];
                const flexibleDigits = hint.locateKind === 'money'
                    || hint.id === 'ssn'
                    || looksLikeSsnQuery(hint.value)
                    || looksLikeMoneyQuery(hint.value);
                for (let q = 0; q < queries.length; q++) {
                    const result = searchDocumentCache(pages, queries[q], undefined, {
                        minLength: 2,
                        flexibleDigits
                    });
                    trunc = trunc || result.truncated;
                    for (let h = 0; h < result.hits.length; h++) {
                        const hit = result.hits[h];
                        const key = hit.pageIndex + ':' + hit.start + ':' + hit.end;
                        if (!hitMap[key]) {
                            hitMap[key] = hit;
                        }
                    }
                }
            }

            const fieldHits = collapseOverlappingHits(Object.keys(hitMap).map(k => hitMap[k]));
            matched.push({
                hint,
                count: fieldHits.length,
                hits: fieldHits,
                truncated: trunc
            });
        }
        setMatchedFields(matched);
    }, [args.textReady, args.mimeKind, args.findHints, args.loading, args.error, args.pageCacheRef, args.scanTick]);

    useEffect(() => {
        if (!args.autoSelectRequired) {
            return;
        }
        if (args.loading || args.error || !args.textReady || !matchedFields.length) {
            return;
        }
        if (matchedFields.length !== args.findHints.length) {
            return;
        }
        const signature = matchedFields.map(f => f.hint.id + ':' + f.count).join(',');
        const key = (args.url || '') + '|' + signature;
        if (autoSelectKeyRef.current === key) {
            return;
        }
        const required = matchedFields.filter(f => f.hint.priority === 'required'
            || (f.hint.priority !== 'supporting' && !f.hint.relatedTo));
        const requiredPool = required.length
            ? required
            : matchedFields.filter(f => f.hint.priority !== 'supporting');
        const exactFields = requiredPool.filter(f => f.count > 0);
        const softFields = matchedFields.filter(f => {
            if (f.count <= 0 || f.hint.priority !== 'supporting' || !f.hint.relatedTo) {
                return false;
            }
            const parent = matchedFields.find(p => p.hint.id === f.hint.relatedTo);
            return !!(parent && parent.count === 0);
        });
        const combined: FindHit[] = [];
        let trunc = false;
        const seen: { [k: string]: boolean } = {};
        const pushHits = (field: MatchedField, soft: boolean) => {
            trunc = trunc || field.truncated;
            for (let i = 0; i < field.hits.length; i++) {
                const hit = field.hits[i];
                const hitKey = hit.pageIndex + ':' + hit.start + ':' + hit.end + ':' + (soft ? 's' : 'e');
                if (seen[hitKey]) {
                    continue;
                }
                seen[hitKey] = true;
                combined.push(soft ? { ...hit, soft: true } : { ...hit, soft: false });
            }
        };
        exactFields.forEach(f => pushHits(f, false));
        softFields.forEach(f => pushHits(f, true));
        combined.sort((a, b) => {
            const rank = (hit: FindHit) => {
                if (!hit.soft) {
                    return 0;
                }
                if (hit.locateRole === 'label') {
                    return 2;
                }
                return 1;
            };
            const ra = rank(a);
            const rb = rank(b);
            if (ra !== rb) {
                return ra - rb;
            }
            return (a.pageIndex - b.pageIndex) || ((a.start || 0) - (b.start || 0));
        });
        autoSelectKeyRef.current = key;
        if (!combined.length) {
            return;
        }
        const focusField = exactFields[0] || softFields[0];
        const focusId = exactFields[0]
            ? focusField.hint.id
            : `${(focusField.hint.relatedTo || focusField.hint.id)}:likely:0`;
        setActiveFieldId(focusId);
        setLocateSource('field');
        applyHits(combined, trunc, true);
    }, [args.autoSelectRequired, args.url, matchedFields, args.findHints, args.textReady, args.loading, args.error, applyHits]);

    useEffect(() => {
        hitsRef.current = hits;
        activeIndexRef.current = activeIndex;
        args.paintHighlights();
    }, [hits, activeIndex, args.paintHighlights]);

    const activateHit = useCallback((index: number, focus: boolean) => {
        const list = hitsRef.current;
        if (!list.length) {
            return;
        }
        const next = ((index % list.length) + list.length) % list.length;
        const hit = list[next];
        setActiveIndex(next);
        activeIndexRef.current = next;
        if (focus) {
            pendingFocusRef.current = true;
        }
        if (hit.pageIndex !== args.pageIndex) {
            args.setPageIndex(hit.pageIndex);
        } else {
            args.paintHighlights();
        }
        setSearchStatus(
            truncated
                ? `Match ${next + 1} of ${list.length} (first ${HIT_CAP})`
                : `Match ${next + 1} of ${list.length}`
        );
    }, [args, truncated]);

    const selectMatchedField = (field: MatchedField) => {
        setActiveFieldId(field.hint.id);
        setLocateSource('field');
        if (!field.hits.length) {
            applyHits([], false, false);
            setSearchStatus('No matches');
            return;
        }
        applyHits(field.hits, field.truncated, true);
        if (field.hits[0] && field.hits[0].pageIndex === args.pageIndex) {
            args.paintHighlights();
        }
    };

    const runCustomSearch = () => {
        if (args.mimeKind !== 'pdf' && args.mimeKind !== 'image') {
            setSearchStatus('Find works on text PDFs and scanned images');
            return;
        }
        if (!args.hasText || !args.textReady) {
            setSearchStatus(args.mimeKind === 'image'
                ? (args.ocrRunning ? 'Scanning document…' : 'Waiting for text…')
                : (args.ocrRunning ? 'Scanning page…' : 'No text on this PDF — use Rescan'));
            return;
        }
        const q = customQuery.trim();
        if (q.length < MIN_QUERY_LEN) {
            setSearchStatus(`Type at least ${MIN_QUERY_LEN} characters`);
            return;
        }
        setActiveFieldId(null);
        setLocateSource('custom');
        const result = searchDocumentCache(args.pageCacheRef.current, q, undefined, {
            flexibleDigits: looksLikeSsnQuery(q) || looksLikeMoneyQuery(q)
        });
        applyHits(result.hits, result.truncated, true);
        if (result.hits.length && result.hits[0].pageIndex === args.pageIndex) {
            args.paintHighlights();
        }
    };

    const collectSoftHits = (helpers: MatchedField[]): { hits: FindHit[]; truncated: boolean } => {
        const combined: FindHit[] = [];
        let trunc = false;
        const seen: { [k: string]: boolean } = {};
        for (let i = 0; i < helpers.length; i++) {
            const helper = helpers[i];
            trunc = trunc || helper.truncated;
            for (let h = 0; h < helper.hits.length; h++) {
                const hit = helper.hits[h];
                const hitKey = hit.pageIndex + ':' + hit.start + ':' + hit.end;
                if (seen[hitKey]) {
                    continue;
                }
                seen[hitKey] = true;
                combined.push({ ...hit, soft: true });
            }
        }
        return { hits: combined, truncated: trunc };
    };

    const activateLikelyHit = (parent: MatchedField, helpers: MatchedField[], hitIndex: number) => {
        const collected = collectSoftHits(helpers);
        if (!collected.hits.length) {
            return;
        }
        const focusId = `${parent.hint.id}:likely:${hitIndex}`;
        setActiveFieldId(focusId);
        setLocateSource('field');
        applyHits(collected.hits, collected.truncated, false);
        window.setTimeout(() => activateHit(hitIndex, true), 0);
    };

    const canSearch = !args.loading && !args.error && args.hasText && args.textReady && (args.mimeKind === 'pdf' || args.mimeKind === 'image');
    const showLocate = args.showLocate !== false
        && !args.loading
        && !args.error
        && (args.mimeKind === 'pdf' || (args.mimeKind === 'image' && (args.findHints.length > 0 || !!args.allowOcr)));
    const locatePending = !args.error && (
        args.loading
        || args.aligning
        || args.ocrRunning
        || (args.mimeKind === 'image' && !!args.allowOcr && !args.textReady && !args.ocrNote)
    );
    const locateUnavailable = !locatePending && !canSearch;
    const hasVerifyPriority = args.findHints.some(h => h.priority === 'required' || h.priority === 'supporting');
    const matchedLabel = args.verifyLabel
        ? `Verify · ${args.verifyLabel}`
        : (args.mimeKind === 'image' ? 'Expected fields' : 'Profile fields');
    const requiredFields = hasVerifyPriority
        ? matchedFields.filter(f => f.hint.priority === 'required')
        : matchedFields;
    const supportingFields = hasVerifyPriority
        ? matchedFields.filter(f => f.hint.priority === 'supporting')
        : [];
    const requiredFoundCount = locatePending
        ? 0
        : requiredFields.filter(f => f.count > 0).length;
    const requiredLikelyCount = locatePending
        ? 0
        : requiredFields.filter(f => {
            if (f.count > 0) {
                return false;
            }
            return supportingFields.some(s => s.hint.relatedTo === f.hint.id && s.count > 0);
        }).length;
    const verifyProgressLabel = locatePending
        ? ' · Checking…'
        : (requiredLikelyCount > 0 && requiredFoundCount < requiredFields.length
            ? ` · ${requiredFoundCount} exact · ${requiredLikelyCount} format match${requiredLikelyCount === 1 ? '' : 'es'}`
            : ` · ${requiredFoundCount}/${requiredFields.length}`);
    const orphanSupporting = !locatePending
        ? supportingFields.filter(f => !f.hint.relatedTo && f.count > 0)
        : [];
    const activeLabel = locateSource === 'custom'
        ? (customQuery.trim() || 'Custom')
        : (matchedFields.find(f => f.hint.id === activeFieldId)?.hint.label || '');

    return {
        hits,
        hitsRef,
        activeIndex,
        activeIndexRef,
        pendingFocusRef,
        truncated,
        searchStatus,
        setSearchStatus,
        activeFieldId,
        matchedFields,
        customQuery,
        setCustomQuery,
        locateSource,
        clear,
        activateHit,
        selectMatchedField,
        runCustomSearch,
        collectSoftHits,
        activateLikelyHit,
        goPrevHit: () => activateHit(activeIndex - 1, true),
        goNextHit: () => activateHit(activeIndex + 1, true),
        canSearch,
        showLocate,
        locatePending,
        locateUnavailable,
        matchedLabel,
        requiredFields,
        supportingFields,
        verifyProgressLabel,
        orphanSupporting,
        activeLabel
    };
}
